import { useAppDispatch } from "hooks/redux";
import { SectionViewResizeHandle } from "./SectionViewResizeHandle";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TWebsiteBuilderSelectedNodeEditingMode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSelectedNode";
import { TWebsiteBuilderTreeRefMap } from "pages/secure/facility/websiteBuilder/editor/hooks/treeRefMap/useTreeRefMapRegistration";
import {
  websiteBuilderResizeSection,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { clamp, max } from "lodash";
import { WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY as SECTION_CHILD_PLACEMENT_BOUNDARY } from "../../../../../nodes/section/websiteBuilderSection";

export interface IUseSectionViewResizeArgs {
  id: string;
  hovered: boolean;
  resizing: boolean;
  height: number;
  childrenIds: string[];
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

interface ISectionViewResizeState {
  grabbing: boolean;
  height: number;
}

interface ISectionViewResizeRefState {
  currentTarget: Element;
  lowerClampHeight: number;
  prevHeight: number;
  nextHeight: number;
  resizing: boolean;
}

interface IUseSectionViewResize {
  viewResizeHeight: number;
  viewResizeHandle: JSX.Element;
}

const MINIMUM_SECTION_HEIGHT = 130;

export function useSectionViewResize(args: IUseSectionViewResizeArgs): IUseSectionViewResize {
  const dispatch = useAppDispatch();

  const [resizeState, setResizeState] = useState<ISectionViewResizeState>({
    grabbing: false,
    height: null,
  });

  const resizeRefState = useRef<ISectionViewResizeRefState>({
    currentTarget: null,
    lowerClampHeight: null,
    prevHeight: null,
    nextHeight: null,
    resizing: false,
  });

  const viewResizeHeight: number = useMemo(() => {
    if (args.resizing && resizeState.height != null) {
      return resizeState.height;
    } else {
      return args.height;
    }
  }, [args.resizing, resizeState.height, args.height]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const { lowerClampHeight, resizing } = resizeRefState.current;

      if (lowerClampHeight == null) {
        return;
      }

      const sectionRef = args.treeRefMap.current.get(args.id);
      const sectionBoundingRect = sectionRef?.getBoundingClientRect();

      if (!sectionBoundingRect) {
        return;
      }

      const yDelta = e.clientY - sectionBoundingRect.bottom;
      const updatedHeight = sectionBoundingRect.height + yDelta;

      const clampedUpdatedHeight = clamp(updatedHeight, lowerClampHeight, Infinity);

      resizeRefState.current.nextHeight = clampedUpdatedHeight;

      setResizeState(prev => ({
        ...prev,
        height: clampedUpdatedHeight,
      }));

      if (!resizing) {
        resizeRefState.current.resizing = true;

        dispatch(
          websiteBuilderUpdateSelectedNode({
            selectedNodeUpdates: {
              editingMode: "section-resize",
            },
          }),
        );
      }
    },
    [args.id],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const { resizing, prevHeight, nextHeight, currentTarget } = resizeRefState.current;

      if (resizing) {
        if (prevHeight != null && nextHeight != null && Math.abs(prevHeight - nextHeight) > 1) {
          dispatch(
            websiteBuilderResizeSection({
              id: args.id,
              prevHeight,
              nextHeight,
            }),
          );
        } else {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                editingMode: "none",
              },
            }),
          );
        }
      }

      currentTarget?.releasePointerCapture(e.pointerId);

      resizeRefState.current = {
        currentTarget: null,
        lowerClampHeight: null,
        prevHeight: null,
        nextHeight: null,
        resizing: false,
      };

      setResizeState({
        grabbing: false,
        height: null,
      });
    },
    [args.id],
  );

  useEffect(() => {
    if (resizeState.grabbing) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resizeState.grabbing, handlePointerMove, handlePointerUp]);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) {
      return;
    }

    const sectionRef = args.treeRefMap.current.get(args.id);
    const sectionBoundingRect = sectionRef?.getBoundingClientRect();

    const childrenBoundingRects = args.childrenIds.map(childId =>
      args.treeRefMap.current.get(childId)?.getBoundingClientRect(),
    );

    if (!sectionBoundingRect || childrenBoundingRects.some(rect => !rect)) {
      return;
    }

    const relativeChildrenY = childrenBoundingRects.map(rect => rect.bottom - sectionBoundingRect.top);
    const lowerClampChildY = (max(relativeChildrenY) ?? 0) + SECTION_CHILD_PLACEMENT_BOUNDARY;
    const lowerClampHeight = Math.max(MINIMUM_SECTION_HEIGHT, lowerClampChildY);

    e.currentTarget.setPointerCapture(e.pointerId);

    resizeRefState.current.currentTarget = e.currentTarget;

    resizeRefState.current.lowerClampHeight = lowerClampHeight;

    resizeRefState.current.prevHeight = args.height;
    resizeRefState.current.nextHeight = args.height;

    resizeRefState.current.resizing = false;

    setResizeState(prev => ({
      ...prev,
      grabbing: true,
    }));
  }

  return {
    viewResizeHeight,
    viewResizeHandle: (
      <SectionViewResizeHandle hovered={args.hovered} grabbing={resizeState.grabbing} onPointerDown={onPointerDown} />
    ),
  };
}
